import React, { useEffect, useState } from 'react';
import { TrendingUp, ArrowUpRight } from 'lucide-react';
interface FeaturedMetricProps {
  title: string;
  value: string;
  subtitle: string;
  trend: string;
  activeFeature: number;
}
const featuredMetrics: Record<number, FeaturedMetricProps> = {
  0: {
    title: 'Total Pipeline Value',
    value: '$2.4M',
    subtitle: 'Across 89 active deals',
    trend: '+15%'
  },
  1: {
    title: 'Win Rate',
    value: '27%',
    subtitle: 'Best performing quarter',
    trend: '+5%'
  },
  2: {
    title: 'Deals Closing Soon',
    value: '8',
    subtitle: 'Expected this week',
    trend: '+3'
  },
  3: {
    title: 'Top Market ROI',
    value: '+23%',
    subtitle: 'Austin, TX leading',
    trend: '+5%'
  }
};
/**
 * FeaturedMetric component
 * Large, prominent metric display with animation
 */
export function FeaturedMetric({
  activeFeature
}: {
  activeFeature: number;
}) {
  const metric = featuredMetrics[activeFeature];
  const [displayValue, setDisplayValue] = useState('0');
  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setDisplayValue(metric.value);
      setIsAnimating(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFeature, metric.value]);
  return <div className="relative bg-gradient-to-br from-[#3ab795]/10 via-[#3ab795]/5 to-transparent border border-[#3ab795]/20 rounded-2xl p-6 overflow-hidden group hover:border-[#3ab795]/40 transition-all duration-500" data-id="element-231">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#3ab795]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-232" />

      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-233">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#3ab795] rounded-full blur-3xl animate-pulse" data-id="element-234" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#3ab795] rounded-full blur-3xl animate-pulse" style={{
        animationDelay: '1s'
      }} data-id="element-235" />
      </div>

      <div className="relative z-10" data-id="element-236">
        {/* Header */}
        <div className="flex items-start justify-between mb-4" data-id="element-237">
          <div className="space-y-1" data-id="element-238">
            <div className="flex items-center gap-2" data-id="element-239">
              <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-240" />
              <span className="text-xs font-medium text-[#3ab795] uppercase tracking-wider" data-id="element-241">
                Featured Metric
              </span>
            </div>
            <h3 className="text-sm font-medium text-white/80" data-id="element-242">
              {metric.title}
            </h3>
          </div>

          {/* Trend Badge */}
          <div className="flex items-center gap-1 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full" data-id="element-243">
            <ArrowUpRight className="w-3 h-3 text-[#3ab795]" data-id="element-244" />
            <span className="text-sm font-bold text-[#3ab795]" data-id="element-245">
              {metric.trend}
            </span>
          </div>
        </div>

        {/* Value */}
        <div className="mb-3" data-id="element-246">
          <div className={`text-5xl font-bold text-white transition-all duration-500 ${isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`} style={{
          textShadow: '0 2px 20px rgba(58, 183, 149, 0.3)'
        }} data-id="element-247">
            {displayValue}
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-[#b8bcc8] flex items-center gap-2" data-id="element-248">
          {metric.subtitle}
          <TrendingUp className="w-4 h-4 text-[#3ab795]" data-id="element-249" />
        </p>

        {/* Progress bar */}
        <div className="mt-4 h-1 bg-white/5 rounded-full overflow-hidden" data-id="element-250">
          <div className="h-full bg-gradient-to-r from-[#3ab795] to-[#2d9f7e] rounded-full transition-all duration-1000 ease-out" style={{
          width: isAnimating ? '0%' : '75%'
        }} data-id="element-251" />
        </div>
      </div>
    </div>;
}